import * as React from 'react'
import { Logger } from '@utils'
import { PlayerCapabilities, ContentConfig } from 'ATVWebPlayer'
import { ClientsideLogger } from '../../../util/logger/logger'
import { createPlayer, PlayerProxy, HistoryItem } from './player/index'
import { PlayerConfigData } from './player/config'
import { sendStartPlaybackAnalytics } from './player/logger'

if (!global.__SERVER__) {
  require('../utils/polyfill')
}

const LEGACY_DEEPLINK_CLASS = '.js-deeplinkable'
const LEGACY_ATTR_SIGNIN_URL = 'signInUrl'
const LEGACY_ATTR_HREF = 'href'
const LEGACY_ATTR_LCID = 'data-lcid'
const LEGACY_ATTR_ASIN = 'data-asin'
const LEGACY_ATTR_TITLE_ID = 'data-title-id'
const LEGACY_ATTR_RESUME_TIME = 'data-resume-time'
const LEGACY_ATTR_IS_TRAILER = 'isTrailer'
const LEGACY_ATTR_VIDEO_TYPE = 'data-video-type'
const LEGACY_ATTR_CLICKSTREAM_REF = 'data-clickstream-ref'
const LEGACY_ATTR_REF = 'data-ref'

export type PlayerContentConfig = ContentConfig & {
  lcid?: string
  ref?: string
}

export type PlaybackContentLink = {
  contentConfig: PlayerContentConfig
  signInUrl?: string
  playbackUrl: string
}

export type PlayerIntegrationProps = {
  logger?: Logger
  userAgent: string
  playerConfig: PlayerConfigData
  playbackLinkData: PlaybackContentLink | undefined
  whisperCacheData: PlayerContentConfig | undefined
  onAsinChanged?: (config: HistoryItem) => unknown
  onPlay?: (config: PlayerContentConfig) => unknown
  onPlayFailure?: () => void
  onClosed?: (config: HistoryItem) => unknown
  onInitialized?: () => void
  onPrepare?: () => void
  onAborted?: () => void
  onPlayerCapabilities?: (capabilities: PlayerCapabilities) => void
  liveIncompatibleCallback: () => void
}

const isAndroid = (userAgent: string) => userAgent.match(/\bAndroid\b/)

export const navigate = (url: string) => {
  window.location.assign(url)
}

export class PlayerIntegration extends React.Component<PlayerIntegrationProps> {
  private readonly logger: Logger
  private player?: PlayerProxy

  constructor(props: PlayerIntegrationProps) {
    super(props)
    this.logger = props.logger || new ClientsideLogger('PlayerIntegration')
  }

  initializeLegacyPlaybackSupport() {
    //used for Hover old and new
    document.addEventListener('click', event => {
      const element =
        event && (event.target as Element).closest(LEGACY_DEEPLINK_CLASS)
      if (element) {
        const attr = (attr: string) => element.getAttribute(attr)
        const parentAttr = (attr: string) =>
          element.parentElement && element.parentElement.getAttribute(attr)
        const signInUrl =
          attr(LEGACY_ATTR_SIGNIN_URL) ||
          parentAttr(LEGACY_ATTR_SIGNIN_URL) ||
          undefined
        const playbackUrl = attr(LEGACY_ATTR_HREF)!
        const lcid = attr(LEGACY_ATTR_LCID) || undefined
        const asin = attr(LEGACY_ATTR_ASIN) || attr(LEGACY_ATTR_TITLE_ID)!
        const position = parseInt(attr(LEGACY_ATTR_RESUME_TIME) || '0')
        const trailer = Boolean(attr(LEGACY_ATTR_IS_TRAILER))
        const videoType = attr(LEGACY_ATTR_VIDEO_TYPE)!
        const clickstreamRefMarker =
          attr(LEGACY_ATTR_CLICKSTREAM_REF) ||
          attr(LEGACY_ATTR_REF) ||
          undefined

        const contentConfig: PlayerContentConfig = {
          asin,
          trailer,
          position,
          ref: clickstreamRefMarker,
          videoType,
          lcid,
        }

        this.handlePlayRequest({ contentConfig, signInUrl, playbackUrl })
        event.preventDefault()
      }
    })
  }

  getPlayer() {
    if (!this.player) {
      const error = 'DVWebPlayer:notReady'
      this.logger.count(error, 1, true).fatal(error)
      return null
    }
    return this.player
  }

  attachRefreshEventHandlers() {
    const {
      onPlay,
      onClosed,
      onAsinChanged,
      onInitialized,
      onPrepare,
      onAborted,
      onPlayerCapabilities,
    } = this.props

    const player = this.player!

    onPlay && player.on('play', onPlay)
    onClosed && player.on('closed', onClosed)
    onAsinChanged && player.on('asinChanged', onAsinChanged)
    onInitialized && player.on('initialized', onInitialized)
    onPrepare && player.on('prepare', onPrepare)
    onAborted && player.on('aborted', onAborted)
    onPlayerCapabilities && player.capabilities.then(onPlayerCapabilities)
  }

  async componentDidMount() {
    const {
      playerConfig,
      whisperCacheData,
      liveIncompatibleCallback,
    } = this.props
    try {
      this.player = await createPlayer(
        playerConfig,
        this.logger,
        liveIncompatibleCallback
      )
      this.attachRefreshEventHandlers()
      this.initializeLegacyPlaybackSupport()
      if (whisperCacheData) {
        await this.handlePrepareRequest(whisperCacheData)
      }
    } catch (error) {
      this.logger.count(error, 1, true).fatal(error)
    }
  }

  componentDidUpdate({
    playbackLinkData: previousLinkData,
    whisperCacheData: previousWhisperCacheData,
  }: PlayerIntegrationProps) {
    const { playbackLinkData, whisperCacheData } = this.props
    if (playbackLinkData && !previousLinkData) {
      this.handlePlayRequest(playbackLinkData)
    }

    if (
      !playbackLinkData &&
      whisperCacheData &&
      whisperCacheData !== previousWhisperCacheData
    ) {
      this.handlePrepareRequest(whisperCacheData)
    }
  }

  async componentWillUnmount() {
    const player = this.getPlayer()
    player && player.resetPlayer()
  }

  async handlePrepareRequest(contentConfig: PlayerContentConfig) {
    const { userAgent } = this.props
    if (!(isAndroid(userAgent) && !contentConfig.trailer)) {
      const player = this.getPlayer()
      player && player.prepare(contentConfig)
    }
  }

  async handlePlayRequest({
    contentConfig,
    signInUrl,
    playbackUrl,
  }: PlaybackContentLink) {
    const { onPlayFailure, userAgent } = this.props
    const { ref, trailer } = contentConfig

    if (ref) {
      sendStartPlaybackAnalytics(ref)
    }

    // The next 2 lines can be removed if experiment controlled by these 3 weblabs is stopped/fails
    // DV_PRIMEVIDEO_LAUNCH_HTML_TRAILER_223433
    // DV_PRIMEVIDEO_HTML_TRAILER_ROW_236326
    // DV_PRIMEVIDEO_HTML_TRAILER_222386
    if (isAndroid(userAgent) && !trailer) {
      navigate(playbackUrl)
    } else if (signInUrl) {
      navigate(signInUrl)
    } else {
      const player = this.getPlayer()
      let playSuccess = false
      if (player) {
        try {
          await player.play(contentConfig)
          playSuccess = true
        } catch (e) {}
      }

      if (!playSuccess && onPlayFailure) {
        onPlayFailure()
      }
    }
  }

  render(): null {
    return null
  }
}
